import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { CARDS, INLINE_CTA } from "../../lib/blog-markers";

export const creerListeDeNoelEnLigne: BlogPost = {
  h1: "Créer une liste de Noël en ligne : le mode d'emploi, étape par étape",
  seoTitle: "Créer une liste de Noël en ligne gratuite : le mode d'emploi · Listou",
  seoDescription:
    "Comment créer sa liste de Noël en ligne : quand la commencer, combien d'envies mettre, une liste par enfant ou pour toute la famille, la partager et garder la surprise. Le guide pratique, étape par étape.",
  publishedOn: "2026-10-26",
  updatedOn: "2026-10-26",
  summary:
    "Une liste de Noël en ligne se crée en quelques minutes : on colle le lien des cadeaux souhaités, de n'importe quelle boutique, et on envoie un seul lien à la famille. Chacun réserve le cadeau qu'il offre, ce qui évite les doublons, et la personne qui reçoit ne voit pas ce qui a été pris. Commencez-la début novembre et envoyez-la à la mi-novembre, quand la famille commence à chercher.",
  body: `
## Pourquoi faire sa liste de Noël en ligne

La liste sur papier, ou dans un message envoyé à la famille, a deux défauts. Personne ne sait ce que les autres ont acheté, et la liste n'est plus à jour dès qu'on change d'avis. Résultat : deux fois le même jeu sous le sapin, et le pull qui plaisait en octobre n'existe plus en décembre.

Une liste en ligne règle les deux. Chaque proche **réserve** le cadeau qu'il offre, et tout le monde voit qu'il est pris. Vous modifiez la liste quand vous voulez : le lien reste le même.

## Étape 1 : quand commencer

**Début novembre** pour la créer, **mi-novembre** pour l'envoyer. C'est le moment où les grands-parents et les oncles commencent à demander « qu'est-ce qui lui ferait plaisir ? ». Les achats en ligne arrivent sans stress avant le 24, et les promotions de fin novembre profitent à ceux qui offrent.

Rien n'oblige à tout remplir d'un coup : une première dizaine d'envies suffit pour l'envoyer, le reste s'ajoute au fil des semaines.

## Étape 2 : une liste par personne, ou une pour toute la famille

C'est le choix qui compte le plus à Noël, parce qu'une liste ne vit jamais seule.
${CARDS}
Le groupe est la meilleure solution dès que plusieurs personnes s'offrent des cadeaux entre elles : personne n'a à gérer cinq liens différents.

## Étape 3 : ajouter les envies

Pour chaque cadeau, collez le **lien du produit**, de n'importe quelle boutique : le nom, la photo et le prix se remplissent tout seuls. Une envie sans lien fonctionne aussi : « un livre de cuisine végétarienne », « des chaussettes rigolotes ».

Quelques règles pour une liste qui fonctionne :

- **Entre 10 et 20 envies** par adulte, un peu plus pour un enfant, pour que les derniers à ouvrir la liste aient encore le choix.
- **Des prix étalés** : quelques petits cadeaux sous 20 €, des cadeaux moyens, et un ou deux plus gros à offrir à plusieurs.
- **Les détails dans la note** : la taille, la couleur, le tome déjà possédé. C'est ce qui évite le pull à échanger.
- **Les priorités** : sur Listou, chaque envie se marque **Coup de cœur**, **Envie** ou **Idée**, pour que les proches sachent par où commencer.

Pour un gros cadeau, une console ou un vélo, donnez une **quantité** à l'envie : un vélo à 300 € en six parts de 50 € se réserve part par part.
${INLINE_CTA}
## Étape 4 : partager la liste

Un seul lien suffit, à envoyer par message, par email ou dans la conversation de famille. Les proches n'ont **ni compte à créer ni application à installer** : ils ouvrent le lien, choisissent un cadeau et le réservent en un clic.

Pour les grands-parents moins à l'aise avec un téléphone, deux solutions :

- leur montrer la liste sur le vôtre, et réserver pour eux ;
- dans un groupe familial, remplir leur propre liste à leur place, s'ils veulent aussi recevoir des cadeaux.

## Étape 5 : garder la surprise

Une liste de Noël n'est pas un bon de commande. Sur Listou, vous ne voyez pas ce qui a été réservé sur votre propre liste : vous savez seulement que la famille s'en occupe. Dans un groupe, chacun voit ce qui est pris sur les listes des autres, jamais sur la sienne.

Pour les enfants, c'est encore plus important : la liste reste celle du Père Noël, et les parents peuvent la tenir sans gâcher la magie de leurs propres cadeaux.

## Étape 6 : relancer et mettre à jour

Trois semaines avant Noël, Listou vous envoie un rappel, pour avoir le temps de renvoyer le lien à ceux qui ne l'ont pas encore ouvert. C'est aussi le moment de retirer ce que vous avez acheté vous-même, et d'ajouter deux ou trois idées si tout est déjà réservé.

Après les fêtes, archivez la liste. L'an prochain, une nouvelle se crée en deux minutes.

## Les erreurs à éviter

- **Envoyer la liste trop tard.** Après le 10 décembre, une partie de la famille a déjà acheté au hasard.
- **Ne mettre que des gros cadeaux.** La cousine étudiante ne trouvera rien, et se sentira obligée de dépenser trop.
- **Oublier les détails.** « Un pyjama » sans taille, c'est un pyjama à échanger.
- **Faire une liste par personne sans les réunir.** Avec cinq liens, quelqu'un en oublie toujours un.

Pour choisir l'outil, notre [comparatif des listes de Noël en ligne](/blog/meilleure-liste-de-noel) passe dix services en revue. Et pour les enfants, notre guide des [idées de liste de Noël par âge](/blog/liste-de-noel-enfant-idees-par-age) donne des pistes de 1 à 14 ans.
`,
  faq: [
    [
      "Comment faire une liste de Noël en ligne gratuitement ?",
      "Sur Listou, créez une liste de Noël, collez le lien des cadeaux souhaités depuis n'importe quelle boutique, puis envoyez le lien à vos proches. C'est gratuit, et vos proches n'ont pas de compte à créer pour réserver.",
    ],
    [
      "Quand envoyer sa liste de Noël ?",
      "À la mi-novembre, quand la famille commence à chercher. Créez-la début novembre, et complétez-la au fil des semaines : le lien reste le même.",
    ],
    [
      "Combien de cadeaux mettre sur une liste de Noël ?",
      "Entre 10 et 20 pour un adulte, un peu plus pour un enfant, avec des prix étalés : quelques petits cadeaux, des cadeaux moyens, et un ou deux gros à offrir à plusieurs.",
    ],
    [
      "Comment faire une liste de Noël pour toute la famille ?",
      "Créez un groupe de listes et envoyez son lien : chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Un seul lien pour tout le monde, au lieu d'une liste par personne à faire circuler.",
    ],
    [
      "Peut-on voir qui a réservé un cadeau sur sa liste de Noël ?",
      "Sur Listou, non : vous ne voyez pas ce qui a été réservé sur votre propre liste, pour garder la surprise. Dans un groupe familial, chacun voit ce qui est pris sur les listes des autres, jamais sur la sienne.",
    ],
    [
      "Peut-on mettre un cadeau sans lien sur sa liste ?",
      "Oui. Une envie peut être une simple phrase, « un livre de cuisine » ou « des chaussettes rigolotes », avec une note pour préciser ce que vous aimez.",
    ],
  ],
  occasion: "christmas",
  cards: [
    {
      title: "Une liste pour vous",
      text: "Le cas le plus simple : un lien, vos envies.",
      sticker: stickerUrl("noel", "chaussette"),
    },
    {
      title: "Une liste par enfant",
      text: "Chacun la sienne, à son nom et à son image. Les grands-parents savent ce qui est pour qui.",
      sticker: stickerUrl("noel", "bonhomme"),
    },
    {
      title: "Un groupe pour la famille",
      text: "Un seul lien : on choisit son prénom, on remplit sa liste, on réserve sur celles des autres.",
      sticker: stickerUrl("noel", "sapin"),
    },
  ],
  inlineCta: {
    title: "Votre liste de Noël en deux minutes",
    text: "Collez vos liens, envoyez-en un seul à la famille. Personne n'a de compte à créer.",
    sticker: stickerUrl("noel", "cadeau"),
  },
};
